import React, { useEffect, useState } from 'react';
import '../styles/SideBar.css';
import {
  FaSignOutAlt,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES, adminPath } from '../constants/adminPortal';
import { graphqlRequest } from '../utils/graphqlRequest';
import { logoutAdmin } from '../utils/subscriptionAccess';

const PROFILE_SHELL_PATH_RE =
  /^\/(profile-dashboard(?:\/[^/]+)?|profile|account-settings|notifications|help-support|activity-overview|device-sessions|user-activity|dashboard-preferences|data-usage-policy|terms-conditions|faq|report-issue|new-ticket|active-sessions|login-history|role-profiles|branch-management)\/?$/;

/** Matches `/support-management` and legacy `/admin/support-management`. */
const SUPPORT_MANAGEMENT_PATH_RE =
  /^\/(admin\/)?support-management(\/.*)?$/;

const PAGE_KEYS = new Set([
  'dashboard',
  'pipeline',
  'lead',
  'proposals',
  'Contract',
  'services',
  'calendar',
  'chat',
  'project',
  'allInvoice',
  'customer',
  'team-member',
  'workforce-dashboard',
  'licences-compliance',
  'availability',
  'gps-tracking',
  'rosters',
  'uniform-equipment',
  'attendance',
  'forecasting',
  'recruitment',
  'recruitment-applicants',
  'recruitment-interviews',
  'recruitment-offers',
  'recruitment-background',
  'leave',
  'document-workforce',
  'item-description',
  'stock-levels',
  'stock-movements',
  'suppliers',
  'purchase-orders',
  'equipment',
  'vehicles',
  'allocation',
  'usage-tracking',
  'audits',
  'resource-forecasting',
  'resources-dashboard',
  'inventory-update',
  'finance',
  'employee-payroll',
  'contractor-payroll',
  'workforce',
  'time-card',
  'timesheet',
  'support-management',
]);

const SideBar = ({ setActivePage }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const suppressNavHighlight = PROFILE_SHELL_PATH_RE.test(location.pathname);
  const [isOpen] = useState(true);
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const [proposalsOpen, setProposalsOpen] = useState(false);
  const [workforceOpen, setWorkforceOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [activeItem, setActiveItem] = useState('dashboard');
  const [pipelines, setPipelines] = useState([]);

  const isSupportManagementRoute = SUPPORT_MANAGEMENT_PATH_RE.test(
    location.pathname || '',
  );

  const handleClick = (page) => {
    // Persist stateful dashboard sub-pages in URL so refresh restores correctly.
    const search =
      page && page !== 'dashboard' ? `?page=${encodeURIComponent(page)}` : '';
    navigate({ pathname: '/', search }, { replace: true });
    setActivePage(page);
    setActiveItem(page);
  };

  const itemActive = (key) =>
    !suppressNavHighlight && !isSupportManagementRoute && activeItem === key;

  const sectionActive = (...keys) =>
    !suppressNavHighlight &&
    !isSupportManagementRoute &&
    keys.some((k) => {
      if (k === 'pipeline') {
        return (
          activeItem === 'pipeline' ||
          String(activeItem || '').startsWith('sales-pipeline')
        );
      }
      return activeItem === k;
    });

  const handlePipelineSelect = (p) => {
    const pipelineId = p?.id ? String(p.id) : '';
    const search = pipelineId
      ? `?page=sales-pipeline&pipelineId=${encodeURIComponent(pipelineId)}`
      : '?page=sales-pipeline';
    navigate({ pathname: '/', search }, { replace: true });
    setActivePage({ page: 'sales-pipeline', pipelineId });
    setActiveItem(`sales-pipeline-${p.id}`);
    setPipelineOpen(true);
  };

  // Keep highlight + open sections in sync with the URL / route.
  useEffect(() => {
    if (isSupportManagementRoute) {
      setActiveItem('support-management');
      return;
    }
    if (suppressNavHighlight) return;
    if ((location.pathname || '') !== '/') return;

    const params = new URLSearchParams(location.search || '');
    const page = params.get('page');
    if (!page) {
      setActiveItem('dashboard');
      return;
    }

    if (page === 'sales-pipeline') {
      const pipelineId = params.get('pipelineId') || '';
      setActiveItem(
        pipelineId ? `sales-pipeline-${pipelineId}` : 'sales-pipeline',
      );
      setPipelineOpen(true);
      return;
    }

    if (PAGE_KEYS.has(page)) {
      // Licences & Compliance now lives under Documents
      setActiveItem(
        page === 'licences-compliance' ? 'document-workforce' : page,
      );
      if (page === 'pipeline') setPipelineOpen(true);
      if (page === 'proposals' || page === 'Contract') setProposalsOpen(true);
      if (
        page === 'team-member' ||
        page === 'workforce-dashboard' ||
        page === 'licences-compliance' ||
        page === 'availability' ||
        page === 'gps-tracking' ||
        page === 'rosters' ||
        page === 'uniform-equipment' ||
        page === 'attendance' ||
        page === 'forecasting' ||
        page === 'recruitment' ||
        page === 'recruitment-applicants' ||
        page === 'recruitment-interviews' ||
        page === 'recruitment-offers' ||
        page === 'recruitment-background' ||
        page === 'leave' ||
        page === 'document-workforce' ||
        page === 'workforce' ||
        page === 'time-card'
      ) {
        setWorkforceOpen(true);
      }
      if (
        page === 'item-description' ||
        page === 'stock-levels' ||
        page === 'stock-movements' ||
        page === 'suppliers' ||
        page === 'purchase-orders' ||
        page === 'equipment' ||
        page === 'vehicles' ||
        page === 'allocation' ||
        page === 'usage-tracking' ||
        page === 'audits' ||
        page === 'resource-forecasting' ||
        page === 'resources-dashboard' ||
        page === 'inventory-update'
      ) {
        setResourcesOpen(true);
      }
      if (page === 'item-description' || page === 'inventory-update') {
        setInventoryOpen(true);
      }
      if (page === 'employee-payroll' || page === 'contractor-payroll') {
        setPayrollOpen(true);
      }
    }
  }, [location.pathname, location.search, isSupportManagementRoute, suppressNavHighlight]);

  const handleLogout = logoutAdmin;

  useEffect(() => {
    const fetchPipelines = async () => {
      try {
        const query = `
          query FindAllPipeline {
            findAllPipeline {
              id
              name
            }
          }
        `;
        const data = await graphqlRequest(query);
        setPipelines(
          Array.isArray(data?.findAllPipeline) ? data.findAllPipeline : [],
        );
      } catch (err) {
        console.error('Error fetching pipelines:', err);
      }
    };
    fetchPipelines();
  }, []);

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div>
        <button
          type="button"
          className={itemActive('dashboard') ? 'active' : ''}
          onClick={() => handleClick('dashboard')}
        >
          Dashboard
        </button>

        <button
          type="button"
          className={itemActive('lead') ? 'active' : ''}
          onClick={() => handleClick('lead')}
        >
          Lead
        </button>

        <button
          type="button"
          onClick={() => {
            setPipelineOpen(!pipelineOpen);
            handleClick('pipeline');
          }}
          className={`with-arrow${sectionActive('pipeline') ? ' active' : ''}`}
        >
          Pipeline {pipelineOpen ? <FaChevronUp /> : <FaChevronDown />}
        </button>

        {pipelineOpen && (
          <div className="submenu">
            {pipelines.map((p) => (
              <button
                type="button"
                key={p.id}
                className={itemActive(`sales-pipeline-${p.id}`) ? 'active' : ''}
                onClick={() => handlePipelineSelect(p)}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => setProposalsOpen(!proposalsOpen)}
          className={`with-arrow${
            sectionActive('proposals', 'Contract') ? ' active' : ''
          }`}
        >
          Proposals {proposalsOpen ? <FaChevronUp /> : <FaChevronDown />}
        </button>
        {proposalsOpen && (
          <div className="submenu">
            <button
              type="button"
              className={itemActive('proposals') ? 'active' : ''}
              onClick={() => handleClick('proposals')}
            >
              Proposals
            </button>
            <button
              type="button"
              className={itemActive('Contract') ? 'active' : ''}
              onClick={() => handleClick('Contract')}
            >
              Contract
            </button>
          </div>
        )}

        <button
          type="button"
          className={itemActive('services') ? 'active' : ''}
          onClick={() => handleClick('services')}
        >
          Services
        </button>

        <button
          type="button"
          className={itemActive('calendar') ? 'active' : ''}
          onClick={() => handleClick('calendar')}
        >
          Calendar
        </button>

        <button
          type="button"
          className={itemActive('chat') ? 'active' : ''}
          onClick={() => handleClick('chat')}
        >
          Chat
        </button>

        <button
          type="button"
          className={itemActive('project') ? 'active' : ''}
          onClick={() => handleClick('project')}
        >
          Project
        </button>
        <button
          type="button"
          className={itemActive('allInvoice') ? 'active' : ''}
          onClick={() => handleClick('allInvoice')}
        >
          All Invoices
        </button>

        <button
          type="button"
          className={itemActive('customer') ? 'active' : ''}
          onClick={() => handleClick('customer')}
        >
          Customer
        </button>

        <button
          type="button"
          onClick={() => setWorkforceOpen(!workforceOpen)}
          className={`with-arrow${
            sectionActive(
              'team-member',
              'workforce-dashboard',
              'licences-compliance',
              'availability',
              'gps-tracking',
              'rosters',
              'uniform-equipment',
              'attendance',
              'forecasting',
              'recruitment',
              'recruitment-applicants',
              'recruitment-interviews',
              'recruitment-offers',
              'recruitment-background',
              'leave',
              'document-workforce',
              'workforce',
              'time-card',
            )
              ? ' active'
              : ''
          }`}
        >
          Work Force {workforceOpen ? <FaChevronUp /> : <FaChevronDown />}
        </button>
        {workforceOpen && (
          <div className="submenu">
            <button
              type="button"
              className={itemActive('workforce-dashboard') ? 'active' : ''}
              onClick={() => handleClick('workforce-dashboard')}
            >
              Dashboard
            </button>
            <button
              type="button"
              className={
                itemActive('team-member') || itemActive('workforce')
                  ? 'active'
                  : ''
              }
              onClick={() => handleClick('team-member')}
            >
              Employees
            </button>
            <button
              type="button"
              className={itemActive('document-workforce') ? 'active' : ''}
              onClick={() => handleClick('document-workforce')}
            >
              Documents
            </button>
            <button
              type="button"
              className={
                itemActive('recruitment') ||
                itemActive('recruitment-applicants') ||
                itemActive('recruitment-interviews') ||
                itemActive('recruitment-offers') ||
                itemActive('recruitment-background')
                  ? 'active'
                  : ''
              }
              onClick={() => handleClick('recruitment')}
            >
              Recruitment
            </button>
            <button
              type="button"
              className={itemActive('availability') ? 'active' : ''}
              onClick={() => handleClick('availability')}
            >
              Availability
            </button>
            <button
              type="button"
              className={itemActive('gps-tracking') ? 'active' : ''}
              onClick={() => handleClick('gps-tracking')}
            >
              GPS Tracking
            </button>
            <button
              type="button"
              className={itemActive('leave') ? 'active' : ''}
              onClick={() => handleClick('leave')}
            >
              Leave Management
            </button>
            <button
              type="button"
              className={itemActive('rosters') ? 'active' : ''}
              onClick={() => handleClick('rosters')}
            >
              Rosters
            </button>
            <button
              type="button"
              className={itemActive('uniform-equipment') ? 'active' : ''}
              onClick={() => handleClick('uniform-equipment')}
            >
              Uniform & Equipment
            </button>
            <button
              type="button"
              className={
                itemActive('attendance') || itemActive('time-card')
                  ? 'active'
                  : ''
              }
              onClick={() => handleClick('attendance')}
            >
              Attendance
            </button>
            <button
              type="button"
              className={itemActive('forecasting') ? 'active' : ''}
              onClick={() => handleClick('forecasting')}
            >
              Forecasting
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => setResourcesOpen(!resourcesOpen)}
          className={`with-arrow${
            sectionActive(
              'item-description',
              'stock-levels',
              'stock-movements',
              'suppliers',
              'purchase-orders',
              'equipment',
              'vehicles',
              'allocation',
              'usage-tracking',
              'audits',
              'resource-forecasting',
              'resources-dashboard',
              'inventory-update',
            )
              ? ' active'
              : ''
          }`}
        >
          Resources {resourcesOpen ? <FaChevronUp /> : <FaChevronDown />}
        </button>
        {resourcesOpen && (
          <div className="submenu">
            <button
              type="button"
              className={itemActive('resources-dashboard') ? 'active' : ''}
              onClick={() => handleClick('resources-dashboard')}
            >
              Dashboard
            </button>
            <button
              type="button"
              onClick={() => {
                if (inventoryOpen) {
                  setInventoryOpen(false);
                  return;
                }
                setInventoryOpen(true);
                handleClick('item-description');
              }}
              className={`with-arrow${
                sectionActive('item-description', 'inventory-update')
                  ? ' active'
                  : ''
              }`}
            >
              Inventory {inventoryOpen ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {inventoryOpen && (
              <div className="submenu-nested">
                <button
                  type="button"
                  className={itemActive('item-description') ? 'active' : ''}
                  onClick={() => handleClick('item-description')}
                >
                  Products
                </button>
                <button
                  type="button"
                  className={itemActive('inventory-update') ? 'active' : ''}
                  onClick={() => handleClick('inventory-update')}
                >
                  Updates
                </button>
              </div>
            )}
            <button
              type="button"
              className={itemActive('stock-levels') ? 'active' : ''}
              onClick={() => handleClick('stock-levels')}
            >
              Stock Levels
            </button>
            <button
              type="button"
              className={itemActive('stock-movements') ? 'active' : ''}
              onClick={() => handleClick('stock-movements')}
            >
              Stock Movements
            </button>
            <button
              type="button"
              className={itemActive('suppliers') ? 'active' : ''}
              onClick={() => handleClick('suppliers')}
            >
              Suppliers
            </button>
            <button
              type="button"
              className={itemActive('purchase-orders') ? 'active' : ''}
              onClick={() => handleClick('purchase-orders')}
            >
              Purchase Orders
            </button>
            <button
              type="button"
              className={itemActive('equipment') ? 'active' : ''}
              onClick={() => handleClick('equipment')}
            >
              Equipment
            </button>
            <button
              type="button"
              className={itemActive('vehicles') ? 'active' : ''}
              onClick={() => handleClick('vehicles')}
            >
              Vehicles
            </button>
            <button
              type="button"
              className={itemActive('allocation') ? 'active' : ''}
              onClick={() => handleClick('allocation')}
            >
              Allocation
            </button>
            <button
              type="button"
              className={itemActive('usage-tracking') ? 'active' : ''}
              onClick={() => handleClick('usage-tracking')}
            >
              Usage Tracking
            </button>
            <button
              type="button"
              className={itemActive('audits') ? 'active' : ''}
              onClick={() => handleClick('audits')}
            >
              Audits
            </button>
            <button
              type="button"
              className={itemActive('resource-forecasting') ? 'active' : ''}
              onClick={() => handleClick('resource-forecasting')}
            >
              Forecasting
            </button>
          </div>
        )}

        <button
          type="button"
          className={itemActive('finance') ? 'active' : ''}
          onClick={() => handleClick('finance')}
        >
          Finance
        </button>

        <button
          type="button"
          onClick={() => setPayrollOpen(!payrollOpen)}
          className={`with-arrow${
            sectionActive('employee-payroll', 'contractor-payroll')
              ? ' active'
              : ''
          }`}
        >
          Payroll {payrollOpen ? <FaChevronUp /> : <FaChevronDown />}
        </button>
        {payrollOpen && (
          <div className="submenu">
            <button
              type="button"
              className={itemActive('employee-payroll') ? 'active' : ''}
              onClick={() => handleClick('employee-payroll')}
            >
              Employee Payroll
            </button>
            <button
              type="button"
              className={itemActive('contractor-payroll') ? 'active' : ''}
              onClick={() => handleClick('contractor-payroll')}
            >
              Contractor Payroll
            </button>
          </div>
        )}

        <button
          type="button"
          className={
            isSupportManagementRoute || itemActive('support-management')
              ? 'active'
              : ''
          }
          onClick={() => {
            setActiveItem('support-management');
            navigate(adminPath(ADMIN_ROUTES.SUPPORT_MANAGEMENT));
          }}
        >
          Support Management
        </button>

        <button
          type="button"
          className="logout-btn-sidebar"
          onClick={handleLogout}
        >
          <FaSignOutAlt style={{ marginRight: '8px' }} />
          Logout
        </button>
      </div>
    </aside>
  );
};

export default SideBar;
